import React, { useState, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from "primereact/button";
import { ProgressBar } from "primereact/progressbar";

const options = {
  poster: { width: 300, height: 450 },
  thumb: { width: 2057, height: 1080 },
  lista: { width: 768, height: 272 },
  header: { width: 1440, height: 900 },
};

const qualityMapping = {
  poster: 0.9,
  thumb: 0.9,
  lista: 0.9,
  header: 0.9,
};

const BatchArtExtractor = () => {
  const [imdbCodes, setImdbCodes] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [processedResults, setProcessedResults] = useState([]);
  const [autoProcess, setAutoProcess] = useState(true);
  const [log, setLog] = useState([]);

  // Fixed to poster
  const selectedOption = 'poster';
  const { width, height } = options[selectedOption];
  const lambdaUrl = 'https://c4tbcewui32jxfefhfpeufkj7a0wcxut.lambda-url.us-east-1.on.aws/';
  // Base URL for our API
  const apiBaseUrl = 'http://vodcore.backend.sofadigital.com/'; // Change as needed

  // Function to add entries to the log
  const addToLog = (message) => {
    setLog(prevLog => [...prevLog, `[${new Date().toLocaleTimeString()}] ${message}`]);
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target.result;
      
      // Improved parsing logic to handle both formats:
      // 1. One IMDb code per line
      // 2. Concatenated codes without separators
      
      // First try to split by newlines
      let candidates = content.split('\n')
        .map(line => line.trim())
        .filter(line => line.length > 0);
      
      // If we only have one line, it might be concatenated codes
      if (candidates.length === 1 && candidates[0].length > 9) {
        // Extract all IMDb codes using a regex pattern
        const imdbPattern = /tt\d{6,8}/g;
        const matches = candidates[0].match(imdbPattern) || [];
        
        if (matches.length > 0) {
          candidates = matches;
        }
      }
      
      // Filter to ensure all are valid IMDb codes
      const codes = candidates.filter(code => code.match(/^tt\d+$/));
      
      setImdbCodes(codes);
      addToLog(`File loaded: ${codes.length} valid IMDB codes found`);
    };
    reader.readAsText(file);
  };

  // Image processing functions
  const createImage = url =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = url;
      img.onload = () => resolve(img);
      img.onerror = err => reject(err);
    });

  const dataURLtoBlob = dataurl => {
    const arr = dataurl.split(',');
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new Blob([u8arr], { type: mime });
  };

  const getCroppedImg = async (imgSrc, cropArea) => {
    try {
      const image = await createImage(imgSrc);
      
      // Calculate the best crop automatically
      const originalWidth = image.width;
      const originalHeight = image.height;
      
      // Calculate desired aspect ratio
      const targetRatio = width / height;
      
      // Calculate crop dimensions
      let cropWidth, cropHeight;
      if (originalWidth / originalHeight > targetRatio) {
        // Original image is wider than the target
        cropHeight = originalHeight;
        cropWidth = cropHeight * targetRatio;
      } else {
        // Original image is taller than the target
        cropWidth = originalWidth;
        cropHeight = cropWidth / targetRatio;
      }
      
      // Calculate crop position (centered)
      const cropX = (originalWidth - cropWidth) / 2;
      const cropY = (originalHeight - cropHeight) / 2;
      
      // Create canvas and apply crop
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      ctx.drawImage(
        image,
        cropX, cropY, cropWidth, cropHeight,
        0, 0, width, height
      );
      
      // Convert to blob
      return new Promise((resolve, reject) => {
        if (canvas.toBlob) {
          canvas.toBlob(
            blob => {
              if (!blob) reject(new Error('Canvas is empty'));
              else resolve(blob);
            },
            'image/webp',
            qualityMapping[selectedOption]
          );
        } else {
          const dataUrl = canvas.toDataURL('image/webp', qualityMapping[selectedOption]);
          resolve(dataURLtoBlob(dataUrl));
        }
      });
    } catch (err) {
      console.error('Error processing image:', err);
      throw err;
    }
  };

  // Function to get vendor_id from IMDb code
  const getVendorIdByImdbCode = async (imdbCode) => {
    try {
      addToLog(`Fetching vendor_id for ${imdbCode}`);
      const response = await fetch(`${apiBaseUrl}/get-vendor-id-by-imdb-code/${imdbCode}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching vendor_id: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.vendor_id) {
        throw new Error('vendor_id not found');
      }
      
      addToLog(`vendor_id found: ${data.vendor_id}`);
      return data.vendor_id;
    } catch (err) {
      addToLog(`Error fetching vendor_id: ${err.message}`);
      throw err;
    }
  };

  const downloadImage = async (blob, imdbId) => {
    try {
      // Get the vendor_id using the IMDb code
      const vendorId = await getVendorIdByImdbCode(imdbId);
      
      // Use vendor_id in the filename
      const urlBlob = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      link.download = `${vendorId}_poster.webp`;
      link.click();
      URL.revokeObjectURL(urlBlob);
      
      return vendorId;
    } catch (err) {
      // If vendor_id fetch fails, use IMDb code as fallback
      addToLog(`Using IMDb code as fallback for filename: ${err.message}`);
      const urlBlob = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = urlBlob;
      link.download = `${imdbId}_poster.webp`;
      link.click();
      URL.revokeObjectURL(urlBlob);
      
      throw err;
    }
  };

  const processImdbImage = async (imdbId, imageUrl) => {
    try {
      addToLog(`Processing image for ${imdbId}`);
      const blob = await getCroppedImg(imageUrl);
      
      try {
        const vendorId = await downloadImage(blob, imdbId);
        return { imdbId, vendorId, success: true, url: imageUrl };
      } catch (err) {
        return { imdbId, success: true, url: imageUrl, vendorIdError: err.message };
      }
    } catch (err) {
      addToLog(`Error processing ${imdbId}: ${err.message}`);
      return { imdbId, success: false, error: err.message };
    }
  };

  const fetchImdbImages = async (imdbId) => {
    try {
      addToLog(`Fetching images for ${imdbId}`);
      const response = await fetch(`${lambdaUrl}?imdbId=${imdbId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP Error: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (!data.images || !Array.isArray(data.images) || data.images.length === 0) {
        throw new Error('No images found');
      }
      
      // Select the first image
      return data.images[0];
    } catch (err) {
      throw new Error(`Failed to fetch images for ${imdbId}: ${err.message}`);
    }
  };

  const processNextImdb = async () => {
    if (currentIndex >= imdbCodes.length) {
      setProcessing(false);
      addToLog('Processing completed');
      return;
    }

    const imdbId = imdbCodes[currentIndex];
    try {
      const imageUrl = await fetchImdbImages(imdbId);
      const result = await processImdbImage(imdbId, imageUrl);
      setProcessedResults(prev => [...prev, result]);
    } catch (err) {
      addToLog(`Error: ${err.message}`);
      setProcessedResults(prev => [...prev, { imdbId, success: false, error: err.message }]);
    }

    // Update progress
    const newProgress = Math.round(((currentIndex + 1) / imdbCodes.length) * 100);
    setProgress(newProgress);
    setCurrentIndex(prev => prev + 1);
  };

  // Effect for automatic processing when needed
  useEffect(() => {
    if (processing && autoProcess && currentIndex < imdbCodes.length) {
      const timer = setTimeout(() => {
        processNextImdb();
      }, 1000); // Delay to avoid too many simultaneous requests
      
      return () => clearTimeout(timer);
    }
  }, [processing, currentIndex, autoProcess, imdbCodes.length]);

  const startProcessing = () => {
    setProcessing(true);
    setCurrentIndex(0);
    setProgress(0);
    setProcessedResults([]);
    addToLog('Starting batch processing');
  };

  const stopProcessing = () => {
    setProcessing(false);
    addToLog('Processing stopped');
  };

  // Export results as CSV
  const exportResults = () => {
    const csvContent = 'IMDB ID,Vendor ID,Status,URL/Error\n' + 
      processedResults.map(result => 
        `${result.imdbId},${result.vendorId || 'N/A'},${result.success ? 'Success' : 'Failure'},${result.success ? result.url : result.error}`
      ).join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'processing_results.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-4">
      <h2 className="text-xl font-bold mb-4">Batch Image Extractor</h2>
      
      {/* Settings */}
      <div className="mb-4 p-4 border rounded">
        <h3 className="text-lg font-semibold mb-2">Settings</h3>
        
        <div className="mb-2">
          <label className="block mb-1">Output format:</label>
          <div className="p-2 border rounded w-full max-w-md bg-gray-100">
            Poster: 300x450 (webp up to 30kb)
          </div>
        </div>
        
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="autoProcess"
            checked={autoProcess}
            onChange={(e) => setAutoProcess(e.target.checked)}
            disabled={processing}
            className="mr-2"
          />
          <label htmlFor="autoProcess">Processamento automático</label>
        </div>
      </div>
      
      {/* File upload */}
      <div className="mb-4 p-4 border rounded">
        <h3 className="text-lg font-semibold mb-2">Load IMDB Codes List</h3>
        <p className="mb-2 text-sm text-gray-600">
          Upload a TXT file with IMDB codes (format ttXXXXXXX) - one per line or concatenated
        </p>
        
        <input
          type="file"
          accept=".txt"
          onChange={handleFileUpload}
          disabled={processing}
          className="mb-2"
        />
        
        {imdbCodes.length > 0 && (
          <div className="text-sm">
            {imdbCodes.length} IMDB codes loaded
          </div>
        )}
      </div>
      
      {/* Processing controls */}
      <div className="mb-4">
        <Button
          label="Start Processing"
          icon="pi pi-play"
          className="p-button-success mr-2"
          onClick={startProcessing} 
          disabled={processing || imdbCodes.length === 0}
        />
        
        {processing && (
          <Button
            label="Stop Processing"
            icon="pi pi-stop"
            className="p-button-danger mr-2"
            onClick={stopProcessing}
          />
        )}
        
        {!autoProcess && processing && (
          <Button
            label="Process Next"
            icon="pi pi-forward"
            className="p-button-info mr-2"
            onClick={processNextImdb} 
          />
        )}
        
        {processedResults.length > 0 && (
          <Button
            label="Export Results"
            icon="pi pi-download"
            className="p-button-secondary"
            onClick={exportResults}
          />
        )}
      </div>
      
      {/* Progress bar */}
      {processing && (
        <div className="mb-4">
          <div className="mb-1">Progress: {currentIndex} of {imdbCodes.length} ({progress}%)</div>
          <ProgressBar value={progress} />
        </div>
      )}
      
      {/* Results */}
      {processedResults.length > 0 && (
        <div className="mb-4 p-4 border rounded">
          <h3 className="text-lg font-semibold mb-2">Results</h3>
          <div className="max-h-40 overflow-y-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-100">
                  <th className="p-2 text-left">IMDB ID</th>
                  <th className="p-2 text-left">Vendor ID</th>
                  <th className="p-2 text-left">Status</th>
                </tr>
              </thead>
              <tbody>
                {processedResults.map((result, idx) => (
                  <tr key={idx} className={result.success ? 'bg-green-50' : 'bg-red-50'}>
                    <td className="p-2">{result.imdbId}</td>
                    <td className="p-2">{result.vendorId || 'N/A'}</td>
                    <td className="p-2">{result.success ? 'Success' : 'Failure'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Log */}
      <div className="p-4 border rounded">
        <h3 className="text-lg font-semibold mb-2">Log</h3>
        <div className="max-h-40 overflow-y-auto bg-gray-100 p-2 text-sm font-mono">
          {log.map((entry, idx) => (
            <div key={idx}>{entry}</div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BatchArtExtractor;